import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { GripVertical, Maximize2, Pencil, Plus } from 'lucide-react';
import {
  createMetric,
  deleteMetric,
  fetchFeedCounts,
  fetchMetricData,
  listMetrics,
  reorderMetrics,
  updateMetric,
} from '../../api/dispatchMetrics';
import type { DispatchFeedAnswer, DispatchMetric, DispatchMetricData } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { RefreshButton } from '../../components/RefreshButton';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';
import { useTimeFormat } from '../../lib/tenantTime';
import { useDashboardOrigin } from '../comms/booking/useDashboardOrigin';
import { delta, money, exact } from '../analytics/analyticsFormat';
import type { DashboardHost, DispatchBoardFilter, SettingsTarget } from '../../embed/bridge';
import { type RecordTarget, boardHref } from '../comms/booking/dispatchLink';
import { useCapabilities } from '../../lib/capabilities';
import { applyOrder, bandRuns, groupMetrics, moveMetric } from './arrange';
import { answerFor, cardSeries, cardSubtitle, cardTitle, feedSeries } from './cards';
import { greetingFor } from './greeting';
import { hostIcon } from './hostIcons';
import { type CardInputs, cardFace, chartOver } from './cardFace';
import { DashboardPhone } from './DashboardPhone';
import { FaceBody } from './FaceBody';
import {
  type Bucket,
  CONTEXT_DAYS,
  type CivilDay,
  dayEndIso,
  dayStartIso,
  bucketsFor,
  canShiftFrame,
  chartContext,
  dayString,
  naturalBucket,
  resolveFrame,
  shiftFrame,
} from './frames';
import { displayName, isSnapshotMetric, metricTypeOf, subcategoryLabel } from './metricMeta';
import { type BoardLink, MetricDetail } from './MetricDetail';
import { MetricDialog, type MetricWritePayload } from './MetricDialog';
import { type Preset, addInOrder } from './presets';
import { DENSITY_LABELS, DENSITY_MIN_PX, type Density, LIVE_MAX_DAYS, readPrefs, writePrefs } from './prefs';
import { feedKey, planKey, planReads } from './reads';
import { buildChart, buildKpi } from './series';
import { useCompact } from './useCompact';
import { WindowPicker } from './WindowPicker';
import { useLiveRefresh } from './useLiveRefresh';
import './dashboard.css';

/**
 * The operations dashboard.
 *
 * A React rebuild of the dispatch dashboard's own `/dashboard` page, reading
 * the same metric definitions through the same API — so a metric created in
 * either app appears in both, and the order dragged here is the order there.
 *
 * The one thing that is deliberately NOT the same is where the time window
 * lives. There it belongs to each metric; here it belongs to the page, which
 * is what makes two cards comparable at a glance and what collapses a dozen
 * reads into about one per category. See frames.ts for the window and reads.ts
 * for what that saves.
 *
 * A metric's stored `groupBy`/`arrangeBy` are left exactly as they are. They
 * are what the Flutter page still draws from, and opening this screen must not
 * quietly redefine a metric over there.
 */

const BUCKET_LABELS: Record<Bucket, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Yearly',
};

/**
 * The host's header with the senders that act on it, so neither arrives alone:
 * the shortcut runner, and — only where the host said on INIT that it listens —
 * the record and board openers. A null opener means "use the links".
 */
export type DashboardHostProps = DashboardHost & {
  run: (id: string) => void;
  openRecord: ((target: RecordTarget, id: string) => void) | null;
  openDispatch: ((filter: DispatchBoardFilter) => void) | null;
  /** Website analytics' settings, on the host's My Website → Analytics. */
  openSettings: ((target: SettingsTarget) => void) | null;
};

export function DashboardView({
  embedded = false,
  host = null,
  switcher = null,
}: {
  embedded?: boolean;
  /**
   * The section's Operations/Analytics switch (DashboardSection), drawn at the
   * start of the heading row rather than on a row of its own.
   */
  switcher?: React.ReactNode;
  /**
   * The framing app's half of the header — who is signed in and the shortcuts
   * they may use — with the sender that acts on them.
   *
   * Present only inside the dispatch dashboard. Standalone the console has no
   * routes to offer for any of it, so the header is just this screen's own.
   */
  host?: DashboardHostProps | null;
}) {
  const { me } = useCapabilities();
  const { zone, fmtDate } = useTimeFormat();
  // The page's heading. The host's name when framed — it is who the dispatch
  // dashboard says is signed in — else the one `/meta` answers standalone.
  // Told by the tenant's clock, like every other time on the screen.
  const greeting = greetingFor(host?.userName ?? me?.name ?? null, new Date(), zone);

  // Read once, at mount: the frame, the grouping that was chosen FOR it, how
  // wide this reader likes the cards, and whether the page re-reads itself.
  const [prefs, setPrefs] = useState(readPrefs);
  const frameKey = prefs.frame;
  const bucketOverride = prefs.bucket;

  /** Change a preference and remember it in the same breath. */
  const savePrefs = useCallback((patch: Partial<typeof prefs>) => {
    setPrefs((current) => {
      const next = { ...current, ...patch };
      writePrefs(next);
      return next;
    });
  }, []);
  const [custom, setCustom] = useState<{ from: CivilDay; to: CivilDay } | null>(null);
  const [order, setOrder] = useState<number[] | null>(null);
  const [arranging, setArranging] = useState(false);
  const [arrangeError, setArrangeError] = useState<string | null>(null);
  const dragId = useRef<number | null>(null);

  /*
   * The dialog's subject: `undefined` is closed, `null` is a create, and a row
   * is an edit. Three states rather than a pair of booleans, because "open on
   * nothing" and "closed" are what a pair of them gets confused about.
   */
  const [editing, setEditing] = useState<DispatchMetric | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DispatchMetric | null>(null);
  /** How far an add has got — a two-dozen-card run is too long to spin silently. */
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);

  /** The card whose figure is being taken apart, or null. */
  const [opened, setOpened] = useState<DispatchMetric | null>(null);
  const origin = useDashboardOrigin(embedded);
  /**
   * A phone gets its own screen, and a view-only one (see DashboardPhone). The
   * reads, the window and every figure stay here, so the two layouts cannot
   * come to disagree about a number.
   */
  const compact = useCompact();

  const frame = useMemo(
    () =>
      resolveFrame(frameKey, zone, {
        custom: custom ?? undefined,
        bucketOverride: bucketOverride ?? undefined,
      }),
    [frameKey, zone, custom, bucketOverride],
  );

  const metricsLoad = useLoad(() => listMetrics(), []);
  const canEdit = metricsLoad.data?.canEdit ?? false;
  // No list yet and one on its way — the first read, or a retry after a failed
  // one. A retry is a RELOAD, which useLoad does not mark stale, and without
  // this the page it lands on says "No metrics yet" until the answer arrives.
  const listPending = metricsLoad.stale || (metricsLoad.loading && !metricsLoad.data);

  const metrics = useMemo(
    () => applyOrder(metricsLoad.data?.payload ?? [], order),
    [metricsLoad.data, order],
  );
  const plan = useMemo(() => planReads(metrics), [metrics]);
  const groups = useMemo(() => groupMetrics(metrics), [metrics]);
  const feedRequestKey = useMemo(() => feedKey(plan.feed), [plan.feed]);

  // Keyed on the QUESTION, not on the arrays: two renders may union the same
  // subcategories in a different order, and that must not refetch the page.
  const windowKey = useMemo(() => planKey(plan.window), [plan.window]);
  const compareKey = useMemo(() => planKey(plan.compare), [plan.compare]);
  const snapshotKey = useMemo(() => planKey(plan.snapshot), [plan.snapshot]);

  const windowLoad = useLoad<DispatchMetricData[]>(
    () =>
      plan.window.length
        ? fetchMetricData({
            from: frame.fromIso,
            to: frame.toIso,
            groupBy: frame.bucket,
            requests: plan.window,
          })
        : Promise.resolve([]),
    [windowKey, frame.fromIso, frame.toIso, frame.bucket],
  );

  const compareLoad = useLoad<DispatchMetricData[]>(
    () =>
      plan.compare.length
        ? fetchMetricData({
            from: frame.compare.fromIso,
            to: frame.compare.toIso,
            groupBy: frame.bucket,
            requests: plan.compare,
          })
        : Promise.resolve([]),
    [compareKey, frame.compare.fromIso, frame.compare.toIso, frame.bucket],
  );

  /**
   * What a CHART is drawn over, which is the window unless the window is a
   * single day — one bar is not a chart. See `chartContext`.
   */
  const context = useMemo(() => chartContext(frame), [frame]);
  const contextFrom = useMemo(() => dayStartIso(context.from, zone), [context.from, zone]);
  const contextTo = useMemo(() => dayEndIso(context.to, zone), [context.to, zone]);
  // The widened range is a week, so it reads at days whatever the frame said.
  const contextBucket = context.widened ? naturalBucket(CONTEXT_DAYS) : frame.bucket;

  const contextLoad = useLoad<DispatchMetricData[]>(
    () =>
      context.widened && plan.context.length
        ? fetchMetricData({
            from: contextFrom,
            to: contextTo,
            groupBy: contextBucket,
            requests: plan.context,
          })
        : Promise.resolve([]),
    [windowKey, contextFrom, contextTo, contextBucket, context.widened],
  );

  // The dispatch row. One read of the job board for however many dispatch cards
  // the page carries, and no `groupBy` — the answer is per pickup day and
  // `buildChart` buckets it like any other series.
  const feedLoad = useLoad<DispatchFeedAnswer>(
    () =>
      plan.feed.length
        ? fetchFeedCounts({ from: frame.fromIso, to: frame.toIso, metrics: plan.feed })
        : Promise.resolve({ payload: [], unavailable: null }),
    [feedRequestKey, frame.fromIso, frame.toIso],
  );

  const feedFigures = useMemo(
    () => new Map((feedLoad.data?.payload ?? []).map((figure) => [figure.id, figure])),
    [feedLoad.data],
  );
  const feedIds = useMemo(() => new Set(plan.feed.map((entry) => entry.id)), [plan.feed]);

  /**
   * The board read a second time, over the widened range, for dispatch cards
   * drawn as CHARTS.
   *
   * Only those: a dispatch KPI's figure and its accumulated money are the
   * WINDOW's, and a widened read would answer a different question under the
   * same number. A chart has no such figure — it is all shape — so it is the
   * one dispatch card that gains from the context and loses nothing to it.
   *
   * dispatch metrics are KPI-only at creation, so this is a legacy shape and
   * the second board read almost never happens. When it does it is one read
   * for the page, like the first.
   */
  const feedCharts = useMemo(
    () =>
      plan.feed.filter((entry) => {
        const metric = metrics.find((row) => row.id === entry.id);
        return !!metric && metricTypeOf(metric.type) !== 'kpi';
      }),
    [plan.feed, metrics],
  );
  const feedContextKey = useMemo(() => feedKey(feedCharts), [feedCharts]);

  const feedContextLoad = useLoad<DispatchFeedAnswer>(
    () =>
      context.widened && feedCharts.length
        ? fetchFeedCounts({ from: contextFrom, to: contextTo, metrics: feedCharts })
        : Promise.resolve({ payload: [], unavailable: null }),
    [feedContextKey, contextFrom, contextTo, context.widened],
  );

  // No window at all: an all-time figure is not narrowed by the page's range.
  const snapshotLoad = useLoad<DispatchMetricData[]>(
    () =>
      plan.snapshot.length
        ? fetchMetricData({ from: null, to: null, groupBy: 'all', requests: plan.snapshot })
        : Promise.resolve([]),
    [snapshotKey],
  );

  const reloadAll = useCallback(() => {
    metricsLoad.reload();
    windowLoad.reload();
    compareLoad.reload();
    snapshotLoad.reload();
    feedLoad.reload();
    contextLoad.reload();
    feedContextLoad.reload();
  }, [metricsLoad, windowLoad, compareLoad, snapshotLoad, feedLoad, contextLoad, feedContextLoad]);

  // Asked for, and not fired: a wide window is not refreshed however the
  // toggle is set, so the operator keeps their answer for when they narrow it.
  const liveRuns = prefs.live && frame.days <= LIVE_MAX_DAYS;
  useLiveRefresh(liveRuns, reloadAll);

  /**
   * Move the window one of its own lengths.
   *
   * Stored as the custom range, because a stepped window is no longer the
   * preset it started from — "Today" moved back once is a specific day, and
   * leaving the Today chip lit would be a label that disagrees with the dates
   * under it. Pressing the preset again is how an operator comes back.
   */
  const stepFrame = (direction: -1 | 1) => {
    if (!canShiftFrame(frame, direction, zone)) return;
    const next = shiftFrame(frame, direction);
    setCustom(next);
    // The grouping came from the window that was showing, and the new one is
    // the same LENGTH — so it is the one case where carrying it is right.
    savePrefs({ frame: 'custom' });
  };

  /** A custom range picked whole — both ends at once, then applied, as the phone's sheet does. */
  const applyCustom = (range: { from: CivilDay; to: CivilDay }) => {
    setCustom(range);
    savePrefs({ frame: 'custom', bucket: null });
  };

  const pickFrame = (key: string) => {
    setCustom(null);
    // The bucket belongs to the window that was showing; carrying it onto a new
    // one would silently re-clamp to something nobody picked. It is remembered
    // WITH its frame, so coming back to the page restores the pair — which is a
    // different thing from carrying one across a change of window.
    savePrefs({ frame: key, bucket: null });
  };

  /**
   * Write the dragged order back as dispatch `priority`.
   *
   * Applied locally first so the card lands where it was dropped, then sent.
   * A refusal puts the list back rather than leaving the screen showing an
   * order the server does not hold — a drag that silently did not save is the
   * one failure a shared arrangement cannot afford.
   */
  const persistOrder = async (next: number[], previous: number[] | null) => {
    setOrder(next);
    setArrangeError(null);
    try {
      await reorderMetrics(next);
    } catch (error) {
      setOrder(previous);
      setArrangeError(error instanceof Error ? error.message : 'Could not save that order.');
    }
  };

  /**
   * Write a metric, then re-read the list.
   *
   * The list is re-read rather than patched locally because dispatch is the
   * one that decides what a saved row looks like — it is also what the Flutter
   * page reads, and a card drawn from what we THINK we saved is a card that can
   * disagree with the other screen without anybody finding out.
   *
   * The data loads are left alone on purpose. A new or renamed metric changes
   * the read PLAN, and `planReads` keys the loaders on the question, so the
   * ones whose question actually moved refetch by themselves.
   */
  const persistMetric = async (write: MetricWritePayload) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) await updateMetric(editing.id, write);
      else await createMetric(write);
      setEditing(undefined);
      // A create lands at priority 0, so it appears at the end until somebody
      // drags it — dropping the local order lets the server's own order show.
      setOrder(null);
      metricsLoad.reload();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save that metric.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Add the presets somebody ticked — one at a time, stopping at the first
   * refusal (see `addInOrder`).
   */
  const addPresets = async (presets: Preset[]) => {
    setSaving(true);
    setSaveError(null);
    setAdding({ done: 0, total: presets.length });
    try {
      const outcome = await addInOrder(presets, createMetric, (done) =>
        setAdding({ done, total: presets.length }),
      );
      if (outcome.error) setSaveError(outcome.error);
      else setEditing(undefined);
    } finally {
      setAdding(null);
      setSaving(false);
      // Reloaded even on a failure: the ones that landed are real, and a dialog
      // still offering them is how somebody adds a second copy.
      setOrder(null);
      metricsLoad.reload();
    }
  };

  const removeMetric = async (metric: DispatchMetric) => {
    setSaving(true);
    setSaveError(null);
    try {
      await deleteMetric(metric.id);
      setDeleting(null);
      setEditing(undefined);
      setOrder(null);
      metricsLoad.reload();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not delete that metric.');
      setDeleting(null);
    } finally {
      setSaving(false);
    }
  };

  const onDrop = (toId: number) => {
    const fromId = dragId.current;
    dragId.current = null;
    if (!arranging || fromId === null) return;
    const next = moveMetric(metrics, fromId, toId);
    if (!next) return;
    void persistOrder(next, order);
  };

  const buckets = bucketsFor(frame.days);
  const loadingData = windowLoad.stale || snapshotLoad.stale || feedLoad.stale;
  const rangeLabel =
    frame.days === 1 ? fmtDate(frame.fromIso) : `${fmtDate(frame.fromIso)} – ${fmtDate(frame.toIso)}`;

  /** Everything a card is drawn from — see cardFace.ts, which both layouts render. */
  const cardInputs: CardInputs = {
    frame,
    zone,
    context,
    contextBucket,
    window: windowLoad,
    compare: compareLoad,
    snapshot: snapshotLoad,
    contextRead: contextLoad,
    feed: feedLoad,
    feedContext: feedContextLoad,
    feedIds,
  };

  /**
   * The same numbers the card drew, for the panel that takes them apart.
   *
   * Built from the loaded answers rather than passed down out of `renderCard`,
   * so the panel cannot end up showing a figure the card is not showing — and
   * built HERE rather than in the panel, because which loader answers for a
   * card (window, snapshot or the job board) is this component's question.
   */
  /**
   * A dispatch card's way to the board it counted: one entry per bucket, over
   * the page's window, so the board lists the jobs the card counted.
   *
   * Framed, the host opens its own board (`OPEN_DISPATCH`) and the agent stays
   * in the app they are in; standalone it is the board's address in a new tab.
   * One per bucket because the board filters on one at a time — a card that
   * counts two cannot be shown as one filter, and choosing between them is
   * the reader's call, not a guess made for them.
   */
  const boardLinksFor = (codes: string[]): BoardLink[] => {
    const from = dayString(frame.from);
    const to = dayString(frame.to);
    const openDispatch = host?.openDispatch ?? null;
    return codes.flatMap((bucket) => {
      const open = openDispatch ? () => openDispatch({ bucket, from, to }) : null;
      const href = open ? null : boardHref(origin, bucket, from, to);
      return open || href ? [{ code: bucket, label: subcategoryLabel(bucket), open, href }] : [];
    });
  };

  const detailFor = (metric: DispatchMetric) => {
    const codes = metric.subCategory.filter(Boolean);
    const snapshot = isSnapshotMetric(codes);
    const feed = feedIds.has(metric.id);
    const title = cardTitle(metric);

    if (feed) {
      const found = feedFigures.get(metric.id);
      if (!found) return null;
      const { code, rows } = feedSeries(found, title);
      // A card of several buckets is keyed on its own title, which no record
      // formatter knows — read by that name its jobs rendered as raw
      // key/value, unlinked. They are the board's jobs whatever the key, so the
      // panel reads them as its first bucket's: `Job #…`, opening the job.
      const records = found.codes.length > 1 ? rows.map((row) => ({ ...row, subcategory: found.codes[0] })) : rows;
      return {
        rows: records,
        // The board answers one window at a time and its count is that
        // window's, so there is nothing to compare against and nothing widened.
        compareRows: null,
        compareLabel: frame.compareLabel,
        truncated: !!found.truncated,
        figure: exact(found.count),
        previous: null,
        delta: null,
        // Worded as the card words it: this is the board as it stands, not a
        // figure for all time.
        standing: 'On the board now',
        boardLinks: boardLinksFor(found.codes),
        chart: buildChart({ rows, codes: [code], from: frame.from, to: frame.to, bucket: frame.bucket, zone }),
        highlight: null,
        widened: false,
      };
    }

    const source = snapshot ? snapshotLoad : windowLoad;
    const answer = answerFor(source.data, metric.category);
    // The card's own series, narrowed out of the category batch it shares.
    const rows = cardSeries(answer.series, codes);
    const compareRows = snapshot ? null : answerFor(compareLoad.data, metric.category).series;
    const kpi = buildKpi({ rows, compareRows, codes, snapshot });
    const format = (value: number) => (kpi.kind === 'currency' ? money(value) : exact(value));
    const drawn = snapshot ? null : chartOver(cardInputs, rows, codes, metric.category);
    return {
      rows,
      // The previous window's own rows, so a KPI expand can show what the
      // change is a change FROM — which is the half the card cannot fit.
      compareRows: compareRows ? cardSeries(compareRows, codes) : null,
      compareLabel: frame.compareLabel,
      truncated: false,
      figure: format(kpi.current),
      previous: kpi.previous === null ? null : format(kpi.previous),
      delta: kpi.previous === null ? null : delta(kpi.current, kpi.previous),
      standing: kpi.previous !== null ? null : snapshot ? 'All time, right now' : 'No comparison',
      boardLinks: [],
      chart: drawn?.chart ?? null,
      highlight: drawn?.highlight ?? null,
      widened: !!drawn?.widened,
    };
  };

  /* ---- a single card ------------------------------------------------------ */

  const renderCard = (metric: DispatchMetric) => {
    const type = metricTypeOf(metric.type);
    const codes = metric.subCategory.filter(Boolean);
    const title = cardTitle(metric);
    const subtitle = cardSubtitle(codes, title, metric.category);

    // A KPI opens from anywhere on the card. Charts keep the control in the
    // corner: a chart is read by pointing at a bar, and a click there should
    // not also open the panel. Arrange mode is a drag, so the click stays off.
    const openOnClick = type === 'kpi' && !arranging;

    return (
      <article
        key={metric.id}
        className={`db-card${type === 'kpi' ? ' kpi' : ''}${arranging ? ' arranging' : ''}`}
        draggable={arranging}
        onClick={openOnClick ? () => setOpened(metric) : undefined}
        onDragStart={() => {
          dragId.current = metric.id;
        }}
        onDragOver={(event) => {
          if (arranging) event.preventDefault();
        }}
        onDrop={() => onDrop(metric.id)}
      >
        <header className="db-card-head">
          <div>
            <h3>{title}</h3>
            {/* Titled as well as printed: on a KPI row the list is clamped to
                two lines so the figures stay level, and the hover is where the
                rest of a long one is still readable. */}
            <p title={subtitle}>{subtitle}</p>
          </div>
          {/* Shown on hover outside arrange mode and pinned inside it — a
              hover-only affordance is unreachable on a touch screen, and
              putting these behind a mode is what made Edit look absent. */}
          <span className={arranging ? 'db-cardtools pinned' : 'db-cardtools'}>
            {/* Not gated on canEdit: reading what a figure is made of is a
                READ, and anyone who may see the card may see its rows. */}
            <button
              type="button"
              className="db-cardbtn"
              title="What this is made of"
              aria-label={`What ${title} is made of`}
              onClick={() => setOpened(metric)}
            >
              <Maximize2 size={13} />
            </button>
            {canEdit ? (
              // Edit before drag: the grip is the last thing under the
              // pointer, so the button nearest the card's edge is the one a
              // drag starts from.
              <button
                type="button"
                className="db-cardbtn"
                title="Edit this metric"
                aria-label={`Edit ${title}`}
                onClick={(event) => {
                  // The card around this button opens the figure. Edit is a
                  // different action, so the click must not reach it.
                  event.stopPropagation();
                  setSaveError(null);
                  setEditing(metric);
                }}
              >
                <Pencil size={14} />
              </button>
            ) : null}
            {canEdit && arranging ? (
              <span className="db-grip" aria-hidden="true">
                <GripVertical size={15} />
              </span>
            ) : null}
          </span>
        </header>
        <div className="db-card-body">
          <FaceBody face={cardFace(metric, cardInputs)} />
        </div>
      </article>
    );
  };

  /** The open card taken apart — built once, and drawn full-screen on a phone. */
  const detailPanel = opened
    ? (() => {
        const detail = detailFor(opened);
        if (!detail) return null;
        return (
          <MetricDetail
            key={opened.id}
            metric={opened}
            rows={detail.rows}
            compareRows={detail.compareRows}
            compareLabel={detail.compareLabel}
            frame={frame}
            chart={detail.chart}
            highlight={detail.highlight}
            widened={detail.widened}
            figure={detail.figure}
            previous={detail.previous}
            delta={detail.delta}
            standing={detail.standing}
            truncated={detail.truncated}
            dashboardOrigin={origin}
            openRecord={host?.openRecord ?? null}
            boardLinks={detail.boardLinks}
            compact={compact}
            onClose={() => setOpened(null)}
          />
        );
      })()
    : null;

  /* ---- the page, on a phone ----------------------------------------------- */

  if (compact) {
    // Nothing that writes is handed down: no dialog, no arrange, no delete and
    // no host shortcuts — the phone screen is for reading the board. The
    // server's edit gate is untouched; this layout simply offers nothing to
    // edit, and says where editing lives.
    return (
      <>
        <DashboardPhone
          embedded={embedded}
          switcher={switcher}
          greeting={greeting}
          frame={frame}
          frameKey={frame.key}
          customActive={frame.key === 'custom'}
          rangeLabel={rangeLabel}
          canStepForward={canShiftFrame(frame, 1, zone)}
          buckets={buckets}
          live={liveRuns}
          livePref={prefs.live}
          list={{ loading: listPending, error: metricsLoad.error, retry: metricsLoad.reload }}
          groups={groups}
          faceOf={(metric) => cardFace(metric, cardInputs)}
          refreshing={
            metricsLoad.refreshing ||
            windowLoad.refreshing ||
            compareLoad.refreshing ||
            snapshotLoad.refreshing ||
            feedLoad.refreshing ||
            contextLoad.refreshing ||
            feedContextLoad.refreshing
          }
          onStep={stepFrame}
          onPickFrame={pickFrame}
          onCustom={applyCustom}
          onBucket={(bucket) => savePrefs({ bucket })}
          onLive={(on) => savePrefs({ live: on })}
          onRefresh={reloadAll}
          onOpen={setOpened}
        />
        {detailPanel}
      </>
    );
  }

  /* ---- the page ----------------------------------------------------------- */

  // The switch rides on every state of the page, the loading and the failed
  // one included — a reader stuck on a board that will not load must still be
  // able to step across to analytics.
  const bareHead = switcher ? (
    <header className="db-head">
      <div className="db-head-left">{switcher}</div>
    </header>
  ) : null;

  if (listPending) {
    return (
      <div className={embedded ? 'db embedded' : 'db'}>
        {bareHead}
        <div className="db-centre">
          <Spinner large />
        </div>
      </div>
    );
  }

  if (metricsLoad.error) {
    return (
      <div className={embedded ? 'db embedded' : 'db'}>
        {bareHead}
        <div className="db-centre">
          <ErrorNote error={metricsLoad.error} />
          <RefreshButton refreshing={metricsLoad.refreshing} onClick={metricsLoad.reload} label="Try again" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? 'db embedded' : 'db'}
      // The chart grid auto-fills, so how wide a card is IS how many fit a row.
      // One property on the page rather than a width per card: the card list
      // and its order belong to dispatch and are shared with the Flutter page,
      // and a per-card width would be the one piece of layout a colleague
      // reading the same board could not see.
      style={{ '--db-card-min': `${DENSITY_MIN_PX[prefs.density]}px` } as React.CSSProperties}
    >
      <header className="db-head">
        <div className="db-head-left">
          {/* The greeting IS the heading: "Operations" named a screen the
              reader had already navigated to, and the line under it said
              something about them. One line in the heading's own weight says
              the second thing without spending a line on the first. */}
          {switcher}
          <h1>{greeting}</h1>
        </div>
        <div className="db-head-right">
          {/* The framing app's shortcuts, drawn here and acted on there. The
              list is whatever it sent: a new one appears without a change on
              this side, and one this build has no icon for still draws. */}
          {host?.shortcuts.length ? (
            <>
              <span className="db-head-shortcuts">
                {host.shortcuts.map((shortcut) => {
                  const Icon = hostIcon(shortcut.icon);
                  return (
                    <button
                      key={shortcut.id}
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => host.run(shortcut.id)}
                    >
                      <Icon size={14} />
                      {shortcut.label}
                    </button>
                  );
                })}
              </span>
              <span className="db-head-rule" aria-hidden="true" />
            </>
          ) : null}
          <RefreshButton
            refreshing={windowLoad.refreshing || metricsLoad.refreshing}
            busy={loadingData}
            onClick={reloadAll}
          />
          {canEdit ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setSaveError(null);
                  setEditing(null);
                }}
              >
                <Plus size={14} />
                New metric
              </button>
              {/* Editing a card is on the card itself now, so this button is
                  about the one thing it always was: moving them. */}
              <button
                type="button"
                className={arranging ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={() => setArranging((on) => !on)}
              >
                {arranging ? 'Done' : 'Arrange'}
              </button>
            </>
          ) : null}
        </div>
      </header>

      <div className="db-filters">
        <WindowPicker
          frame={frame}
          frameKey={frameKey}
          customActive={frame.key === 'custom'}
          rangeLabel={rangeLabel}
          canStepForward={canShiftFrame(frame, 1, zone)}
          onStep={stepFrame}
          onPick={pickFrame}
          onCustom={applyCustom}
        />

        {/* Only the groupings this window can honestly be drawn at — see frames.ts. */}
        {buckets.length > 1 ? (
          <div className="db-seg quiet" role="group" aria-label="Grouping">
            {buckets.map((bucket) => (
              <button
                key={bucket}
                type="button"
                className={frame.bucket === bucket ? 'on' : ''}
                onClick={() => savePrefs({ bucket })}
              >
                {BUCKET_LABELS[bucket]}
              </button>
            ))}
          </div>
        ) : null}

        <div className="db-prefs">
          <div className="db-seg quiet" role="group" aria-label="Card width">
            {(Object.keys(DENSITY_LABELS) as Density[]).map((density) => (
              <button
                key={density}
                type="button"
                className={prefs.density === density ? 'on' : ''}
                onClick={() => savePrefs({ density })}
              >
                {DENSITY_LABELS[density]}
              </button>
            ))}
          </div>

          {/* Said as what it costs rather than as a feature: every tick is a
              batch of reads against somebody else's service. Past a few days
              it stops firing and says so — one more job on a thirty-day count
              moves the third digit, and the dispatch row re-reads the whole
              job board to find it. */}
          <label
            className={liveRuns ? 'db-live' : 'db-live off'}
            title={
              liveRuns
                ? 'Re-reads every 90 seconds while this tab is open'
                : `Only windows of ${LIVE_MAX_DAYS} days or fewer keep themselves up to date`
            }
          >
            <input
              type="checkbox"
              checked={prefs.live}
              onChange={(event) => savePrefs({ live: event.target.checked })}
            />
            {liveRuns ? 'Keep up to date' : 'Live on short windows'}
          </label>
        </div>
      </div>

      {arrangeError ? <Note tone="warn">{arrangeError}</Note> : null}
      {arranging ? (
        <Note tone="info">
          Drag a card onto another to swap their places. The order is shared — it moves on the dispatch
          dashboard too.
        </Note>
      ) : null}

      {!metrics.length ? (
        <div className="db-centre db-empty">
          <Note tone="info">
            {canEdit
              ? 'No metrics yet. Start from a ready-made set, or build your own — either way it is the same list the dispatch dashboard draws.'
              : 'No metrics yet. Somebody with dashboard editing can add them, here or on the dispatch dashboard.'}
          </Note>
          {canEdit ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setSaveError(null);
                setEditing(null);
              }}
            >
              <Plus size={14} />
              Add metrics
            </button>
          ) : null}
        </div>
      ) : null}

      {bandRuns(groups).map((run) => {
        const bands = run.groups.map((group) => (
          <section
            key={group.key}
            className={`db-band ${group.kind}`}
            // What the band is WORTH in cards, so the flex row can tell whether
            // the next one fits beside it. Read by the CSS, which turns it into
            // a width; counting in the stylesheet is not something CSS can do.
            style={
              group.kind === 'kpi'
                ? ({ '--db-kpi-count': group.metrics.length } as React.CSSProperties)
                : undefined
            }
          >
            {group.label ? <h2 className="db-band-title">{group.label}</h2> : null}
            <div className={group.kind === 'kpi' ? 'db-kpi-row' : 'db-grid'}>
              {group.metrics.map(renderCard)}
            </div>
          </section>
        ));
        // Consecutive KPI bands share a wrapping row; a chart band is its own
        // section, since its grid already fills the width.
        return run.kind === 'kpi' ? (
          <div key={run.key} className="db-kpi-bands">
            {bands}
          </div>
        ) : (
          bands
        );
      })}

      {/*
        * The metric dialog. `editing === undefined` is closed; `null` creates.
        * Keyed on the subject so reopening on a different card remounts the
        * form rather than showing the last card's picks under a new title.
        */}
      {editing !== undefined ? (
        <MetricDialog
          key={editing?.id ?? 'new'}
          metric={editing}
          metrics={metrics}
          busy={saving}
          error={saveError}
          progress={adding}
          onSave={persistMetric}
          onAddPresets={addPresets}
          onCancel={() => setEditing(undefined)}
          onDelete={editing ? () => setDeleting(editing) : undefined}
        />
      ) : null}

      {/* What a figure is made of: the rows dispatch counted, each linking at
          its own dashboard route. A read, so it needs no edit permission. */}
      {detailPanel}

      {/* Deleting is confirmed by NAME: the list is shared with the Flutter
          dashboard, so this takes the card off somebody else's screen too. */}
      {deleting ? (
        <ConfirmDialog
          title="Delete this metric?"
          // One <p>, not a fragment: `.dialog-body` is a flex COLUMN, so a bare
          // <strong> beside its own sentence becomes a separate flex item and
          // the sentence breaks away from the name it is about.
          body={
            <p>
              <strong>{displayName(deleting.metricsName)}</strong> comes off this dashboard and off the
              dispatch dashboard — they read the same list. Nothing it counted is affected.
            </p>
          }
          confirmLabel="Delete"
          danger
          busy={saving}
          onConfirm={() => void removeMetric(deleting)}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}
